import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AppRoutes,
  AppVersion,
  AuthRoutes,
  ENV_CONFIG,
  EnvConfig,
  LegalRoutes,
  PLATFORM_TOKEN,
  PlatformProvider
} from '@core';
import { Observable, take } from 'rxjs';
import { AuthWithAppleHandler } from '../../application/handlers/auth-with-apple.handler';
import { AuthWithGoogleHandler } from '../../application/handlers/auth-with-google.handler';

@Component({
  selector: 'auth-welcome-page',
  templateUrl: './welcome.page.html',
  styleUrls: ['./welcome.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WelcomePage {
  readonly termsPath: string = `/${AppRoutes.LEGAL}/${LegalRoutes.TERMS}`;
  readonly privacyPath: string = `/${AppRoutes.LEGAL}/${LegalRoutes.PRIVACY}`;
  readonly appVersion: string = AppVersion;

  readonly loading: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);

  private readonly authWithGoogleHandler: AuthWithGoogleHandler = inject(AuthWithGoogleHandler);
  private readonly authWithAppleHandler: AuthWithAppleHandler = inject(AuthWithAppleHandler);
  private readonly platform: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly router: Router = inject(Router);

  // Na local/stage zawsze (testy UI); na prod tylko dla natywnego iOS (App Store Guideline 4.8).
  readonly showAppleSignIn: boolean = !this.env.production || this.platform.isiOS;

  onGoogle(): void {
    if (this.loading()) return;
    this.runAuth((): Observable<void> => this.authWithGoogleHandler.handle());
  }

  onApple(): void {
    if (this.loading()) return;
    this.runAuth((): Observable<void> => this.authWithAppleHandler.handle());
  }

  goToLogin(): void {
    void this.router.navigate([`/${AppRoutes.AUTH}/${AuthRoutes.LOGIN}`]);
  }

  goToRegister(): void {
    void this.router.navigate([`/${AppRoutes.AUTH}/${AuthRoutes.REGISTER}`]);
  }

  private runAuth(action: () => Observable<void>): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    action()
      .pipe(take(1))
      .subscribe({
        complete: (): void => this.loading.set(false),
        error: (err: Error): void => {
          this.loading.set(false);
          this.errorMessage.set(err.message);
        }
      });
  }
}
